/**
 * PhaseEngine — Centralized phasing system for Condor UI
 *
 * Coordinates phase transitions using DOM transitionend events instead of
 * hardcoded timeouts. Components register with the engine, which monitors
 * their CSS transitions and guarantees exits complete before enters start.
 *
 * Singleton — import as: import phaseEngine from './util/PhaseEngine';
 */

const SAFETY_TIMEOUT_MS = 2000;

class PhaseEngine {
	constructor() {
		this._components = new Map(); // id → { phases, ref, onExitDone, onEnterDone, type }
		this._currentPhase = null;
		this._isTransitioning = false;
		this._transitionId = 0; // monotonic counter for stale detection
		this._pendingExits = new Set();
		this._pendingEnters = new Set();
		this._enteringIds = [];
		this._queuedRequest = null; // last-write-wins for rapid clicks
		this._safetyTimer = null;
		this._listeners = []; // cleanup functions for transitionend listeners
		this._heldNodes = []; // entering nodes with phaseEngine-enter-hold class
		this._initialized = false;
	}

	/**
	 * Initialize the engine with the starting phase.
	 * Called after config loads in App.js.
	 */
	init(startPhase) {
		// Reset prior state so HMR re-init doesn't carry over stale transitions
		this._isTransitioning = false;
		this._pendingExits.clear();
		this._pendingEnters.clear();
		this._enteringIds = [];
		this._queuedRequest = null;
		this._cleanupListeners();
		this._removeEnterHold();
		clearTimeout(this._safetyTimer);
		this._transitionId++;

		this._currentPhase = startPhase;
		this._initialized = true;
		console.log(`[PhaseEngine] Initialized with phase: ${startPhase}`);
	}

	/**
	 * Register a component with the engine.
	 * Returns an unregister function for cleanup in componentWillUnmount.
	 */
	register(id, options) {
		this._components.set(id, { ...options });

		return () => {
			this._components.delete(id);
			const wasExit = this._pendingExits.delete(id);
			const wasEnter = this._pendingEnters.delete(id);
			// If unmounting cleared the last pending component, advance the transition
			if (wasExit && this._pendingExits.size === 0 && this._isTransitioning) {
				this._monitorEnters(this._enteringIds, this._transitionId);
			}
			if (wasEnter && this._pendingEnters.size === 0 && this._isTransitioning) {
				this._completeTransition(this._transitionId);
			}
		};
	}

	// --- Query API ---

	get currentPhase() {
		return this._currentPhase;
	}

	get isTransitioning() {
		return this._isTransitioning;
	}

	shouldShow(id) {
		const comp = this._components.get(id);
		if (!comp) return false;
		if (!comp.phases || comp.phases.length === 0) return true;
		return comp.phases.includes(this._currentPhase);
	}

	// --- Phase Transition API ---

	/**
	 * Request a phase transition. The onReady callback fires immediately
	 * (allowing App.js to setState), then the engine monitors DOM transitions.
	 * If a transition is already in progress, the request is queued (last-write-wins).
	 */
	requestPhase(targetPhase, { force = false, onReady } = {}) {
		if (!this._initialized) {
			console.warn('[PhaseEngine] Not initialized, calling onReady immediately');
			if (onReady) onReady(targetPhase);
			return;
		}

		// Same phase without force: no-op
		if (targetPhase === this._currentPhase && !force) {
			if (onReady) onReady(targetPhase);
			return;
		}

		// Queue if currently transitioning (last-write-wins)
		if (this._isTransitioning) {
			console.log(`[PhaseEngine] Queuing phase: ${targetPhase} (transition in progress)`);
			this._queuedRequest = { targetPhase, force, onReady };
			return;
		}

		this._startTransition(targetPhase, { onReady });
	}

	/**
	 * Hard reset for idle timeout. Clears all listeners, sets phase immediately,
	 * and notifies navigation components to stop transitioning.
	 */
	reset(phase) {
		console.log(`[PhaseEngine] Reset to phase: ${phase}`);
		this._isTransitioning = false;
		this._pendingExits.clear();
		this._pendingEnters.clear();
		this._enteringIds = [];
		this._queuedRequest = null;
		this._cleanupListeners();
		this._removeEnterHold();
		clearTimeout(this._safetyTimer);
		this._transitionId++;
		this._currentPhase = phase;

		// Notify any components with onEnterDone callbacks (e.g. to clear transition state)
		this._components.forEach((comp) => {
			if (comp.onEnterDone) {
				comp.onEnterDone();
			}
		});
	}

	// --- Internal ---

	_startTransition(targetPhase, { onReady }) {
		const prevPhase = this._currentPhase;
		this._currentPhase = targetPhase;
		this._transitionId++;
		const transitionId = this._transitionId;
		this._isTransitioning = true;
		this._pendingExits.clear();
		this._pendingEnters.clear();
		this._cleanupListeners();
		this._removeEnterHold();
		clearTimeout(this._safetyTimer);

		// Classify registered components into exiting / entering / staying
		const exiting = [];
		const entering = [];

		this._components.forEach((comp, id) => {
			if (!comp.phases || comp.phases.length === 0) return; // always visible
			const wasVisible = comp.phases.includes(prevPhase);
			const willBeVisible = comp.phases.includes(targetPhase);
			if (wasVisible && !willBeVisible) exiting.push(id);
			if (!wasVisible && willBeVisible) entering.push(id);
		});

		this._enteringIds = entering;

		const hasTransitions = exiting.length > 0 || entering.length > 0;

		console.log(
			`[PhaseEngine] Transition ${transitionId}: ${prevPhase} → ${targetPhase}` +
			` (exiting: ${exiting.length}, entering: ${entering.length})`
		);

		// Notify navigation components to hide BEFORE onReady triggers React setState.
		// Only hide when actual CSS transitions will occur (exits or enters).
		// This fires in the same synchronous call as onReady, so React batches both
		// setState calls into a single render.
		if (hasTransitions) {
			this._components.forEach((comp) => {
				if (comp.onTransitionStart) comp.onTransitionStart();
			});
		}

		// Fire onReady immediately so App.js can setState (triggers CSSTransition exits/enters)
		if (onReady) onReady(targetPhase);

		// No exiting components → skip straight to monitoring enters
		if (exiting.length === 0) {
			this._monitorEnters(entering, transitionId);
			return;
		}

		// Hold entering components invisible until exits complete.
		// Without this, exits and enters run simultaneously — the only gate is the
		// CSS transition-delay, which provides zero margin on some layouts (hybrid4).
		// Applied synchronously: for unmountOnExit=false components the ref is
		// available now; for unmountOnExit=true the ref is null (they're not in
		// the DOM yet, so no hold is needed — they start invisible on mount).
		this._applyEnterHold(entering);

		// Set up transitionend listeners on exiting component refs
		exiting.forEach(id => this._pendingExits.add(id));

		let hasValidRefs = false;
		exiting.forEach(id => {
			const comp = this._components.get(id);
			if (!comp || !comp.ref?.current) {
				this._pendingExits.delete(id);
				return;
			}

			hasValidRefs = true;
			const node = comp.ref.current;
			const handler = (event) => {
				// Filter: only opacity transitions on the exact registered node
				if (event.target !== node || event.propertyName !== 'opacity') return;
				if (this._transitionId !== transitionId) return; // stale

				this._pendingExits.delete(id);
				if (comp.onExitDone) comp.onExitDone();

				if (this._pendingExits.size === 0) {
					this._monitorEnters(entering, transitionId);
				}
			};

			node.addEventListener('transitionend', handler);
			this._listeners.push(() => node.removeEventListener('transitionend', handler));
		});

		// All refs were invalid → advance immediately
		if (!hasValidRefs && this._pendingExits.size === 0) {
			this._monitorEnters(entering, transitionId);
			return;
		}

		// Safety fallback if transitionend never fires
		this._safetyTimer = setTimeout(() => {
			if (this._transitionId !== transitionId) return;
			if (this._pendingExits.size > 0) {
				console.warn(
					`[PhaseEngine] Safety timeout: ${this._pendingExits.size} exits didn't complete`
				);
				this._pendingExits.clear();
				this._monitorEnters(entering, transitionId);
			}
		}, SAFETY_TIMEOUT_MS);
	}

	_monitorEnters(entering, transitionId) {
		if (this._transitionId !== transitionId) return;

		this._cleanupListeners();
		clearTimeout(this._safetyTimer);

		// No entering components → transition complete
		if (entering.length === 0) {
			this._removeEnterHold();
			this._completeTransition(transitionId);
			return;
		}

		// Remove the enter hold (applied during exit phase) so CSS transitions
		// can start. Force a reflow so the browser recalculates styles after
		// hold removal — without this, the browser may batch the class removal
		// with the already-applied enter-active styles and skip the transition.
		this._removeEnterHold();
		const firstComp = this._components.get(entering[0]);
		if (firstComp?.ref?.current) void firstComp.ref.current.offsetHeight;

		// Defer one frame so the CSS transition actually starts before we
		// attach transitionend listeners (entering components' refs may also
		// still be null at this point if unmountOnExit is true).
		requestAnimationFrame(() => {
			if (this._transitionId !== transitionId) return; // stale after defer

			this._attachEnterListeners(entering, transitionId);
		});
	}

	_attachEnterListeners(entering, transitionId) {
		// Set up transitionend listeners on entering component refs
		entering.forEach(id => this._pendingEnters.add(id));

		let hasValidRefs = false;
		entering.forEach(id => {
			const comp = this._components.get(id);
			if (!comp || !comp.ref?.current) {
				this._pendingEnters.delete(id);
				return;
			}

			hasValidRefs = true;
			const node = comp.ref.current;
			const handler = (event) => {
				if (event.target !== node || event.propertyName !== 'opacity') return;
				if (this._transitionId !== transitionId) return;

				this._pendingEnters.delete(id);

				if (this._pendingEnters.size === 0) {
					this._completeTransition(transitionId);
				}
			};

			node.addEventListener('transitionend', handler);
			this._listeners.push(() => node.removeEventListener('transitionend', handler));
		});

		// All refs were invalid → complete immediately
		if (!hasValidRefs && this._pendingEnters.size === 0) {
			this._completeTransition(transitionId);
			return;
		}

		// Safety fallback for enters
		this._safetyTimer = setTimeout(() => {
			if (this._transitionId !== transitionId) return;
			if (this._pendingEnters.size > 0) {
				console.warn(
					`[PhaseEngine] Safety timeout: ${this._pendingEnters.size} enters didn't complete`
				);
				this._pendingEnters.clear();
				this._completeTransition(transitionId);
			}
		}, SAFETY_TIMEOUT_MS);
	}

	_completeTransition(transitionId) {
		if (this._transitionId !== transitionId) return;

		this._isTransitioning = false;
		this._cleanupListeners();
		clearTimeout(this._safetyTimer);

		// Notify ALL registered components with onEnterDone callbacks
		// (Navigation uses this to clear phaseTransitioning state)
		this._components.forEach((comp) => {
			if (comp.onEnterDone) comp.onEnterDone();
		});

		console.log(`[PhaseEngine] Transition ${transitionId} complete: ${this._currentPhase}`);

		// Process queued request (last-write-wins) — route through requestPhase
		// so the same-phase no-op and force checks still apply.
		if (this._queuedRequest) {
			const { targetPhase, force, onReady } = this._queuedRequest;
			this._queuedRequest = null;
			console.log(`[PhaseEngine] Processing queued phase: ${targetPhase}`);
			this.requestPhase(targetPhase, { force, onReady });
		}
	}

	_applyEnterHold(entering) {
		entering.forEach(id => {
			const comp = this._components.get(id);
			if (comp?.ref?.current) {
				comp.ref.current.classList.add('phaseEngine-enter-hold');
				this._heldNodes.push(comp.ref.current);
			}
		});
	}

	_removeEnterHold() {
		if (this._heldNodes.length === 0) return;
		this._heldNodes.forEach(node => {
			node.classList.remove('phaseEngine-enter-hold');
		});
		this._heldNodes = [];
	}

	_cleanupListeners() {
		this._listeners.forEach(cleanup => cleanup());
		this._listeners = [];
	}
}

const phaseEngine = new PhaseEngine();
export default phaseEngine;
